import { useEffect, useRef, useState, type JSX } from 'react';

import { cn } from '@/lib/utils/cn';

/**
 * TimeField — OS-independent 12-hour time-of-day picker.
 *
 * WHY THIS EXISTS
 * Native `<input type="time">` / `<input type="datetime-local">` render their
 * clock format from the operating-system region setting — macOS shows 12-hour
 * AM/PM, many Windows locales show 24-hour — and there is no HTML/CSS/JS API to
 * force a format on the native widget (Chrome ignores `lang` for time inputs).
 * This control renders three plain `<select>`s whose option labels WE own, so
 * every user sees the same 12-hour `hh : mm AM/PM` UI regardless of platform.
 *
 * It also sidesteps the timezone class of bug: the native `datetime-local`
 * widget speaks local wall-clock, so round-tripping a value through
 * `new Date(...).toISOString()` shifts it by the viewer's UTC offset. This
 * control is pure string arithmetic — no `Date` — so the wall-clock the user
 * picks is exactly what comes back out.
 *
 * VALUE CONTRACT
 * The canonical value is a 24-hour `"HH:mm"` string (e.g. `"08:00"`, `"14:30"`),
 * matching what the bulk-create path and the API already use. Empty string means
 * "no time selected"; the field emits `""` until all three segments are chosen.
 */

const HOUR_OPTIONS = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0')); // 01..12
const MINUTE_OPTIONS = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0')); // 00..59
const MERIDIEM_OPTIONS = ['AM', 'PM'] as const;

interface Segments {
  readonly hour12: string; // '01'..'12' or ''
  readonly minute: string; // '00'..'59' or ''
  readonly meridiem: string; // 'AM' | 'PM' | ''
}

const EMPTY_SEGMENTS: Segments = { hour12: '', minute: '', meridiem: '' };

/** Parse a canonical 24-hour "HH:mm" into 12-hour display segments. */
function parseCanonical(value: string): Segments {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  if (match === null) return EMPTY_SEGMENTS;
  const h24 = Number(match[1]);
  const minute = Number(match[2]);
  if (!Number.isInteger(h24) || h24 < 0 || h24 > 23) return EMPTY_SEGMENTS;
  if (!Number.isInteger(minute) || minute < 0 || minute > 59) return EMPTY_SEGMENTS;
  const meridiem = h24 < 12 ? 'AM' : 'PM';
  const hour12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return {
    hour12: String(hour12).padStart(2, '0'),
    minute: String(minute).padStart(2, '0'),
    meridiem,
  };
}

/** Fold 12-hour display segments back to canonical "HH:mm"; "" until complete. */
function toCanonical(seg: Segments): string {
  if (seg.hour12 === '' || seg.minute === '' || seg.meridiem === '') return '';
  let h24 = Number(seg.hour12) % 12; // 12 -> 0
  if (seg.meridiem === 'PM') h24 += 12; // 12 PM -> 12 (noon); 12 AM -> 0 (midnight)
  return `${String(h24).padStart(2, '0')}:${seg.minute}`;
}

export interface TimeFieldProps {
  /** Id assigned to the hour `<select>` so an external `<label htmlFor>` focuses it. */
  readonly id?: string;
  /** Canonical 24-hour "HH:mm", or "" for unset. */
  readonly value: string;
  /** Emits canonical "HH:mm", or "" while any segment is still unset. */
  readonly onChange: (value: string) => void;
  readonly disabled?: boolean;
  readonly className?: string;
  readonly invalid?: boolean;
  readonly describedBy?: string | undefined;
  /** Root wrapper gets this test id; segments get `-hour` / `-minute` / `-meridiem`. */
  readonly testId?: string;
}

export function TimeField({
  id,
  value,
  onChange,
  disabled = false,
  className,
  invalid = false,
  describedBy,
  testId,
}: TimeFieldProps): JSX.Element {
  const [seg, setSeg] = useState<Segments>(() => parseCanonical(value));
  // Track the last value we emitted so the controlled re-render it triggers does
  // not clobber an in-progress partial selection (e.g. hour chosen, minute not).
  const lastEmitted = useRef<string>(value);

  useEffect(() => {
    if (value === lastEmitted.current) return; // echo of our own emit — ignore
    lastEmitted.current = value;
    setSeg(parseCanonical(value));
  }, [value]);

  function commit(next: Segments): void {
    setSeg(next);
    const canonical = toCanonical(next);
    lastEmitted.current = canonical;
    onChange(canonical);
  }

  const selectClass = cn(
    'h-9 rounded-md border border-input bg-transparent px-2 py-1 text-sm shadow-xs outline-none',
    'focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]',
    'disabled:cursor-not-allowed disabled:opacity-50',
    invalid && 'border-destructive',
  );

  return (
    <div
      data-testid={testId}
      className={cn('flex items-center gap-1.5', className)}
      role="group"
      aria-label="Time"
      aria-describedby={describedBy}
    >
      <select
        id={id}
        className={selectClass}
        disabled={disabled}
        value={seg.hour12}
        onChange={(e) => {
          commit({ ...seg, hour12: e.target.value });
        }}
        aria-label="Hour"
        aria-invalid={invalid || undefined}
        data-testid={testId === undefined ? undefined : `${testId}-hour`}
      >
        <option value="">--</option>
        {HOUR_OPTIONS.map((h) => (
          <option key={h} value={h}>
            {h}
          </option>
        ))}
      </select>
      <span className="text-muted-foreground" aria-hidden="true">
        :
      </span>
      <select
        className={selectClass}
        disabled={disabled}
        value={seg.minute}
        onChange={(e) => {
          commit({ ...seg, minute: e.target.value });
        }}
        aria-label="Minute"
        aria-invalid={invalid || undefined}
        data-testid={testId === undefined ? undefined : `${testId}-minute`}
      >
        <option value="">--</option>
        {MINUTE_OPTIONS.map((m) => (
          <option key={m} value={m}>
            {m}
          </option>
        ))}
      </select>
      <select
        className={selectClass}
        disabled={disabled}
        value={seg.meridiem}
        onChange={(e) => {
          commit({ ...seg, meridiem: e.target.value });
        }}
        aria-label="AM or PM"
        aria-invalid={invalid || undefined}
        data-testid={testId === undefined ? undefined : `${testId}-meridiem`}
      >
        <option value="">--</option>
        {MERIDIEM_OPTIONS.map((mer) => (
          <option key={mer} value={mer}>
            {mer}
          </option>
        ))}
      </select>
    </div>
  );
}
